import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../../Authentication/LanguageContext";
import ServerUrl from "../../../../../BACKEND";
import { FaCrown } from "react-icons/fa"; // Premium Icon
import "./Months.css";

interface MonthItem {
  id: number;
  german: string;
  sentence: string;
  audio: string | null;
  image: string | null;
  tamil: string;
  english: string;
  sinhala: string;
  hindi: string;
  arabic: string;
  turkish: string;
  russian: string;
  chinese: string;
  french: string;
  spanish: string;
  [key: string]: string | number | null;
}

const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const Months: React.FC = () => {
  const [data, setData] = useState<MonthItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const { language } = useLanguage();

  useEffect(() => {
    fetch(`${ServerUrl}/api/months`)
      .then((res) => res.json())
      .then((json) => setData(json))
      .catch((err) => console.error("Error fetching months:", err))
      .finally(() => setLoading(false));
  }, [language]);

  const getTranslation = (item: MonthItem) => {
    const map: Record<string, string> = {
      en: "english",
      de: "german",
      ta: "tamil",
      si: "sinhala",
      hi: "hindi",
      ar: "arabic",
      tr: "turkish",
      ru: "russian",
      zh: "chinese",
      fr: "french",
      es: "spanish",
    };
    return capitalize(String(item[map[language] || "english"] || ""));
  };

  const playAudioOrTTS = (item: MonthItem) => {
    if (currentlyPlaying === item.german) {
      setCurrentlyPlaying(null);
      return;
    }

    setCurrentlyPlaying(item.german);

    if (item.audio) {
      const audio = new Audio(`${ServerUrl}/storage/${item.audio}`);
      audio.play();
      audio.onended = () => setCurrentlyPlaying(null);
      audio.onerror = () => {
        speak(item.german);
      };
    } else {
      speak(item.german);
    }
  };

  const speak = (text: string) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "de-DE";
    utterance.onend = () => setCurrentlyPlaying(null);
    speechSynthesis.speak(utterance);
  };

  const getImageUrl = (image: string | null) => {
    if (!image) return null;
    if (image.startsWith("http")) return image;
    return `${ServerUrl}/storage/${image}`;
  };

  const titleMap = {
    en: "Months",
    de: "Monate",
    ta: "மாதங்கள்",
    si: "මාස",
    hi: "महीने",
    ar: "أشهر",
    tr: "Aylar",
    ru: "Месяцы",
    zh: "月份",
    fr: "Mois",
    es: "Meses",
  };

  const subtitleMap = {
    en: "Months in German",
    de: "Monate auf Deutsch",
    ta: "ஜெர்மனில் மாதங்கள்",
    si: "ජර්මන් මාස",
    hi: "जर्मन में महीने",
    ar: "أشهر بالألمانية",
    tr: "Almanca Aylar",
    ru: "Месяцы на немецком",
    zh: "德语中的月份",
    fr: "Mois en allemand",
    es: "Meses en alemán",
  };

  return (
    <div className="months-page-container">
      <div className="months-page-header">
        <h1 className="months-page-title">{titleMap["de"]}</h1>
        <p className="months-page-subtitle">{subtitleMap[language]}</p>
      </div>

      {loading ? (
        <div className="months-page-loading">Loading...</div>
      ) : (
        <div className="months-page-grid">
          {data.map((item) => {
            const isPlaying = currentlyPlaying === item.german;
            const imageUrl = getImageUrl(item.image);

            return (
              <div
                key={item.id}
                className={`months-page-card ${
                  isPlaying ? "months-page-playing" : ""
                }`}
                onClick={() => playAudioOrTTS(item)}
              >
                <div className="months-page-german-text">
                  {capitalize(item.german)}
                </div>
                <div className="months-page-translation-text">
                  {getTranslation(item)}
                </div>
                <div className="months-page-sentence-text">{item.sentence}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Months;
